import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import CartContext from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { resolveImageUrl } from '../utils/api';
import { lineUnitPrice, lineVariantLabel, groupCartLines } from '../utils/cartLine';

const CartDrawer = ({ isOpen, onClose }) => {
    const { cart, updateCartItem, removeFromCart } = useContext(CartContext);
    const { convertPrice, getCurrencySymbol } = useCurrency();
    const navigate = useNavigate();

    const total = cart?.items?.reduce((sum, item) => {
        const price = lineUnitPrice(item);
        return sum + price * item.quantity;
    }, 0) || 0;

    const handleCheckout = () => {
        navigate('/checkout');
        onClose();
    };

    return (
        <>
            {/* Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40"
                    onClick={onClose}
                />
            )}

            {/* Drawer */}
            <div
                className={`fixed right-0 top-0 h-screen w-full max-w-sm bg-white z-50 transform transition-transform duration-300 ease-in-out flex flex-col overflow-hidden ${
                    isOpen ? 'translate-x-0' : 'translate-x-full'
                }`}
            >
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <h2 className="text-2xl font-light text-black">Cart</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-black transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {!cart || cart.items.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center">
                            <p className="text-gray-600 font-light mb-6">Your cart is empty</p>
                            <button
                                onClick={() => {
                                    navigate('/catalog');
                                    onClose();
                                }}
                                className="bg-black text-white px-6 py-3 hover:bg-gray-900 transition-all duration-300 font-light tracking-wide"
                            >
                                Continue Shopping
                            </button>
                        </div>
                    ) : (
                        // One block per product; variants listed beneath it.
                        <div className="space-y-6">
                            {groupCartLines(cart.items).map(group => (
                                <div key={group.key} className="border-b border-gray-200 pb-6">
                                    <div className="flex gap-3 mb-3">
                                        <img
                                            src={resolveImageUrl(group.product?.image)}
                                            alt={group.product?.name}
                                            className="w-20 h-20 object-cover flex-shrink-0"
                                        />
                                        <div className="min-w-0 flex-grow">
                                            <h3 className="text-sm font-light text-black">{group.product?.name}</h3>
                                            {group.lines.length > 1 && (
                                                <p className="text-xs text-gray-500 font-light mt-1">
                                                    {group.lines.length} options · {getCurrencySymbol()}{convertPrice(group.subtotal)}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        {group.lines.map(item => (
                                            <div key={item.variantId || 'default'} className="flex items-center justify-between gap-2">
                                                <div className="min-w-0">
                                                    {lineVariantLabel(item) && (
                                                        <p className="text-xs font-medium text-gray-700 truncate">{lineVariantLabel(item)}</p>
                                                    )}
                                                    <p className="text-sm text-gray-700 font-light">
                                                        {getCurrencySymbol()}{convertPrice(lineUnitPrice(item))}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                                    <button
                                                        onClick={() => updateCartItem(group.product?._id, item.quantity - 1, item.variantId)}
                                                        className="px-2.5 py-1 border border-gray-300 text-gray-700 hover:border-black transition-all font-light text-sm"
                                                        aria-label="Decrease quantity"
                                                    >
                                                        −
                                                    </button>
                                                    <span className="w-5 text-center font-light text-sm">{item.quantity}</span>
                                                    <button
                                                        onClick={() => updateCartItem(group.product?._id, item.quantity + 1, item.variantId)}
                                                        className="px-2.5 py-1 border border-gray-300 text-gray-700 hover:border-black transition-all font-light text-sm"
                                                        aria-label="Increase quantity"
                                                    >
                                                        +
                                                    </button>
                                                    <button
                                                        onClick={() => removeFromCart(group.product?._id, item.variantId)}
                                                        className="text-gray-400 hover:text-black transition-all ml-1 text-sm"
                                                        aria-label={`Remove ${lineVariantLabel(item) || group.product?.name}`}
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                {cart && cart.items.length > 0 && (
                    <div className="border-t border-gray-200 p-6 bg-white shrink-0">
                        <div className="space-y-4 mb-6">
                            <div className="flex justify-between text-sm text-gray-700 font-light">
                                <span>Subtotal</span>
                                <span>{getCurrencySymbol()}{convertPrice(total)}</span>
                            </div>
                            <div className="flex justify-between text-sm text-gray-700 font-light">
                                <span>Shipping</span>
                                <span>Free</span>
                            </div>
                            <div className="flex justify-between text-base font-light text-black pt-2 border-t border-gray-200">
                                <span>Total</span>
                                <span className="text-lg">{getCurrencySymbol()}{convertPrice(total)}</span>
                            </div>
                        </div>
                        <button
                            onClick={handleCheckout}
                            className="w-full bg-black text-white py-3 hover:bg-gray-900 transition-all duration-300 font-light tracking-wide text-sm"
                        >
                            Checkout
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

export default CartDrawer;
